import {
    type ReactElement,
    type ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";
import type { Task } from "@common/types/task";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { useDynamicTopOffset } from "@design-system/hooks/useDynamicTopOffset";
import { Loader } from "@design-system/Loader/Loader";
import { styleWithVars } from "@lib/cssVars";
import type { TaskBoardTask } from "../types";
import { TaskBoardColumnPanel } from "./TaskBoardColumnPanel";
import { TasksBoard } from "./TasksBoard";
import type { TasksBoardState } from "./useTasksBoardLayout";
import styles from "./TasksBoard.module.scss";

interface TasksBoardContentProps {
    /** Null while the tasks load. */
    tasks: TaskBoardTask[] | null;
    board: Pick<
        TasksBoardState,
        | "display"
        | "model"
        | "onCollapsedChange"
        | "onCardsDropped"
        | "capabilities"
        | "filters"
        | "hasActiveFilters"
    >;
    isCrossWorkspace: boolean;
    openTaskId: string | null;
    onOpenTask: (task: Task) => void;
    onClearFilters: () => void;
    /** Shown above the board, e.g. clients whose tasks failed to load. */
    notice?: ReactNode;
    /** Replaces the board when the picked view itself has no tasks; see `useTaskViewEmptyState`. */
    viewEmptyState?: ReactElement | null;
}

/**
 * What Board shows under the page header: the full board, or with a task
 * open, the narrow panel with that task's column. A closed task's card takes
 * focus back only on the board's first mount after the close.
 */
export const TasksBoardContent = ({
    tasks,
    board: {
        display,
        model,
        onCollapsedChange,
        onCardsDropped,
        capabilities,
        filters,
        hasActiveFilters,
    },
    isCrossWorkspace,
    openTaskId,
    onOpenTask,
    onClearFilters,
    notice,
    viewEmptyState,
}: Readonly<TasksBoardContentProps>) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { topOffset } = useDynamicTopOffset({ containerRef });

    const [previousOpenTaskId, setPreviousOpenTaskId] = useState(openTaskId);
    const [closedTaskId, setClosedTaskId] = useState<string | null>(null);
    if (openTaskId !== previousOpenTaskId) {
        setPreviousOpenTaskId(openTaskId);
        setClosedTaskId(openTaskId === null ? previousOpenTaskId : null);
    }
    const isBoardShown =
        tasks !== null &&
        !viewEmptyState &&
        model.columns.length > 0 &&
        openTaskId === null;
    useEffect(() => {
        if (isBoardShown) {
            setClosedTaskId(null);
        }
    }, [isBoardShown]);

    const renderContent = () => {
        if (tasks === null) {
            return (
                <div className={styles.centered}>
                    <Loader />
                </div>
            );
        }
        if (viewEmptyState) {
            return <div className={styles.centered}>{viewEmptyState}</div>;
        }
        if (model.columns.length === 0) {
            return (
                <div className={styles.centered}>
                    {hasActiveFilters ? (
                        <EmptyState
                            header="No tasks match these filters"
                            action={onClearFilters}
                        />
                    ) : (
                        <EmptyState header="No tasks" />
                    )}
                </div>
            );
        }
        if (openTaskId !== null) {
            return (
                <TaskBoardColumnPanel
                    model={model}
                    openTaskId={openTaskId}
                    cardFields={display.cardFields}
                    isCrossWorkspace={isCrossWorkspace}
                    onOpenTask={onOpenTask}
                />
            );
        }
        return (
            <TasksBoard
                tasks={tasks}
                model={model}
                display={display}
                onCollapsedChange={onCollapsedChange}
                onCardsDropped={onCardsDropped}
                capabilities={capabilities}
                filters={filters}
                isCrossWorkspace={isCrossWorkspace}
                onOpenTask={onOpenTask}
                focusTaskId={closedTaskId}
            />
        );
    };

    return (
        <div
            ref={containerRef}
            className={styles.root}
            style={styleWithVars({
                "--tasks-board-top-offset": `${topOffset}px`,
            })}
        >
            {notice}
            <div className={styles.board}>{renderContent()}</div>
        </div>
    );
};
